/**
 * EmptyStateWelcome - Welcome screen for new conversations
 *
 * Displays agent info, capabilities, and clickable shortcuts when
 * the user opens a new conversation or an empty chat.
 */

import { useEffect, useState } from 'react'
import type { AgentUI } from '../../types/agents'
import type { AgentSnippet } from '../../types/snippets'
import { DynamicIcon } from '@/design-system/atoms/Icon'
import { Spinner } from '@/design-system/atoms/Spinner'
import { snippetsApi } from '../../services/snippetsService'
import { OptimusAvatar } from './OptimusAvatar'

interface EmptyStateWelcomeProps {
  agent: AgentUI | null
  metadata?: Record<string, unknown>
  onSnippetSelect: (snippet: AgentSnippet) => void
  className?: string
}

export function EmptyStateWelcome({
  agent,
  metadata = {},
  onSnippetSelect,
  className = '',
}: EmptyStateWelcomeProps) {
  const [snippets, setSnippets] = useState<AgentSnippet[]>([])
  const [loading, setLoading] = useState(false)

  // Check if chassis is already filled (disable check_chassis tool)
  const chassisNumber = metadata.chassis_number as string | undefined
  const hasCompleteChassis = chassisNumber && chassisNumber.length >= 17

  // Check if a snippet should be disabled based on current metadata
  const isSnippetDisabled = (snippet: AgentSnippet): boolean => {
    // Disable chassis lookup tool if chassis is already complete
    if (hasCompleteChassis && snippet.tool === 'check_chassis') {
      return true
    }
    return false
  }

  const getDisabledReason = (snippet: AgentSnippet): string | null => {
    if (hasCompleteChassis && snippet.tool === 'check_chassis') {
      return 'Chassi já preenchido nos metadados'
    }
    return null
  }

  useEffect(() => {
    if (agent?.type) {
      loadSnippets(agent.type)
    }
  }, [agent?.type])

  const loadSnippets = async (agentCode: string) => {
    setLoading(true)
    try {
      const data = await snippetsApi.getSnippets(agentCode)
      setSnippets(data)
    } catch {
      // Silently fail - snippets are optional
      setSnippets([])
    } finally {
      setLoading(false)
    }
  }

  if (!agent) {
    return null
  }

  return (
    <div
      className={`flex flex-col items-center justify-start h-full px-3 lg:px-4 pt-2 pb-4 overflow-y-auto ${className}`}
    >
      <div className="max-w-4xl w-full">
        {/* Agent Header */}
        <div className="text-center mb-3 lg:mb-4">
          {/* Optimus Avatar */}
          <div className="inline-flex mb-3">
            <OptimusAvatar size="lg" className="shadow-lg" />
          </div>

          <h1 className="text-xl lg:text-2xl font-semibold text-gray-900 dark:text-white mb-2">
            {agent.name}
          </h1>
          <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
            {agent.description ||
              'Selecione uma especialidade abaixo ou digite sua mensagem para começar.'}
          </p>
        </div>

        {/* Capabilities Section */}
        {!loading && snippets.length > 0 && (
          <div className="mb-4 lg:mb-6">
            <h2 className="text-xs lg:text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 text-center">
              Especialidades
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 lg:gap-3">
              {snippets.map(snippet => {
                const disabled = isSnippetDisabled(snippet)
                const disabledReason = getDisabledReason(snippet)

                return (
                  <button
                    key={snippet.code}
                    onClick={() => !disabled && onSnippetSelect(snippet)}
                    disabled={disabled}
                    className={`flex items-start gap-2 lg:gap-3 p-3 lg:p-4 rounded-lg lg:rounded-xl transition-all text-left group ${
                      disabled
                        ? 'bg-gray-100 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 opacity-60 cursor-not-allowed'
                        : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-brand-500 hover:bg-brand-50 dark:hover:bg-brand-900/20'
                    }`}
                  >
                    <span
                      className={`flex-shrink-0 transition-transform ${!disabled && 'group-hover:scale-110'}`}
                    >
                      <DynamicIcon
                        name={snippet.iconName}
                        fallbackValue={snippet.icon}
                        size={24}
                      />
                    </span>
                    <div className="flex-1 min-w-0">
                      <h3
                        className={`font-medium text-xs lg:text-sm ${disabled ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'}`}
                      >
                        {snippet.label}
                      </h3>
                      <p className="text-[11px] lg:text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                        {disabled && disabledReason
                          ? disabledReason
                          : snippet.description}
                      </p>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Loading state for snippets */}
        {loading && (
          <div className="flex justify-center py-8">
            <Spinner size="lg" className="text-brand-500" />
          </div>
        )}
      </div>
    </div>
  )
}

export default EmptyStateWelcome
